import React from 'react';
import { MessageCircle, Bell, CheckCircle2, ChevronRight } from 'lucide-react';
import { Skeleton } from '../Skeleton';

interface AttentionCardProps {
  /** 안 읽은 메시지 합계 — App의 totalUnread(rail 대화방 배지)와 같은 식으로 센다 */
  unreadTotal: number;
  unreadRooms: number;
  /** 안읽음이 있는 방 이름(최근순, 최대 3개) */
  unreadRoomNames: string[];
  activityUnread: number;
  /** 채널 목록을 아직 못 받았으면 0건을 '모두 확인했습니다'로 오해하게 그리지 않는다 */
  ready: boolean;
  onShowUnread?: () => void;
  onOpenActivity?: () => void;
}

/**
 * 행 전체가 버튼이고 오른쪽 '안읽음 보기 >'는 그 모양만 낸 표시다(중첩 버튼 금지).
 * 폰 폭(sm 미만)에서는 라벨을 숨기고 '>'만 남긴다 — 360px에서 라벨 버튼(≈105px)이 제목을 두 줄로 민다.
 */
const rowButton =
  'group w-full flex items-center gap-3.5 px-[18px] py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors';
const rowAction =
  'h-9 shrink-0 flex items-center gap-0.5 text-[13px] font-semibold text-slate-700 sm:pl-3.5 sm:pr-2 sm:rounded-[10px] sm:border sm:border-slate-200 sm:bg-white sm:group-hover:border-slate-300 transition-colors';

/**
 * 홈의 '확인할 것' — 안 읽은 메시지와 알림함을 숫자로만 보여 준다(넓은 홈·한 줄 배치 공용).
 * 메시지 본문은 싣지 않는다(목록 미리보기는 최근 대화 카드가 이미 담당).
 */
export const AttentionCard: React.FC<AttentionCardProps> = React.memo(({
  unreadTotal,
  unreadRooms,
  unreadRoomNames,
  activityUnread,
  ready,
  onShowUnread,
  onOpenActivity,
}) => {
  const fmt = (n: number) => (n > 999 ? '999+' : n.toLocaleString('ko-KR'));
  const allClear = unreadTotal === 0 && activityUnread === 0;

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <h2 className="px-[18px] pt-4 pb-1 text-sm font-bold text-slate-600">확인할 것</h2>

      {!ready ? (
        <div className="flex items-center gap-3.5 px-[18px] pt-3 pb-4">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
      ) : allClear ? (
        <div className="flex items-center gap-3.5 px-[18px] pt-3 pb-4">
          <span className="w-10 h-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 size={20} />
          </span>
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-slate-800">모두 확인했습니다</p>
            <p className="mt-0.5 text-[13px] text-slate-500">새 메시지와 알림이 오면 여기에 표시됩니다</p>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {unreadTotal > 0 && (
            <AttentionRow
              icon={<MessageCircle size={20} />}
              title={<>안 읽은 메시지 <span className="font-extrabold text-brand-700">{fmt(unreadTotal)}건</span></>}
              detail={
                `대화방 ${unreadRooms}곳${unreadRoomNames.length > 0 ? ` · ${unreadRoomNames.join(', ')}` : ''}`
                + (unreadRooms > unreadRoomNames.length ? ' 외' : '')
              }
              actionLabel="안읽음 보기"
              onClick={onShowUnread}
            />
          )}
          {activityUnread > 0 && (
            <AttentionRow
              icon={<Bell size={20} />}
              title={<>알림함 <span className="font-extrabold text-brand-700">{fmt(activityUnread)}건</span></>}
              detail="나를 언급한 메시지·답글·반응·전자결재"
              actionLabel="알림함 열기"
              onClick={onOpenActivity}
            />
          )}
        </div>
      )}
    </section>
  );
});
AttentionCard.displayName = 'AttentionCard';

/** '확인할 것'의 한 줄. onClick이 없으면 누를 수 없는 정보 행으로만 그린다 */
const AttentionRow: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  detail: string;
  actionLabel: string;
  onClick?: () => void;
}> = ({ icon, title, detail, actionLabel, onClick }) => {
  const body = (
    <>
      <span className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 min-w-0 flex flex-col">
        <span className="text-[15px] font-semibold text-slate-800">{title}</span>
        <span className="mt-0.5 text-[13px] text-slate-500 truncate">{detail}</span>
      </span>
    </>
  );
  if (!onClick) {
    return <div className="flex items-center gap-3.5 px-[18px] py-3">{body}</div>;
  }
  return (
    <button type="button" onClick={onClick} className={rowButton}>
      {body}
      {/* 폰 폭에서는 라벨을 화면에서만 감춘다 — 스크린리더에는 '안읽음 보기'까지 읽힌다 */}
      <span className={rowAction}>
        <span className="sr-only sm:not-sr-only">{actionLabel}</span>
        <ChevronRight size={16} aria-hidden="true" className="text-slate-500 sm:text-current" />
      </span>
    </button>
  );
};
